import React from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

import Checkbox from "@/components/atoms/Checkbox";
import FormControl, { FormControlProps } from "@/components/molecules/form/FormControl";

export type Option<T = unknown> = {
  id: string | number;
  value: T;
  label?: string;
};

export interface FormOptionsCheckboxesProps<T = unknown> extends Omit<FormControlProps, "id" | "children"> {
  name: string;
  options: Option<T>[];
  rules?: RegisterOptions;
  /**
   * Optional custom renderer for the label.
   * Receives the current option; fallbacks to option.label or String(option.value) if omitted.
   */
  getLabel?: (option: Option<T>) => React.ReactNode;
}

const FormOptionsCheckboxes = <T,>({ name, options, rules, getLabel, ...controls }: FormOptionsCheckboxesProps<T>) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        return (
          <FormControl {...controls} id={name} error={fieldState.error?.message}>
            <div className="grid gap-2">
              {options.map((option, index) => {
                const checked = (field.value as Option<T>[]).some((o) => o.id === option.id);

                const toggle = () =>
                  field.onChange(
                    checked
                      ? (field.value as Option<T>[]).filter((o) => o.id !== option.id)
                      : [...(field.value as Option<T>[]), option],
                  );

                const label = getLabel?.(option) ?? option.label ?? String(option.value);

                return (
                  <label
                    key={String(option.value)}
                    htmlFor={`${name}-opt-${index}`}
                    className="flex items-center gap-3"
                  >
                    <Checkbox id={`${name}-opt-${index}`} checked={checked} onChange={toggle} />
                    <span className="text-sm font-medium text-secondary">{label}</span>
                  </label>
                );
              })}
            </div>
          </FormControl>
        );
      }}
    />
  );
};

export default FormOptionsCheckboxes;
